import React, { useState, useCallback, useEffect } from 'react';
import StarryBackground from '../components/StarryBackground';
import MagicBall from '../components/MagicBall';
import QuestionInput from '../components/QuestionInput';
import AnswerHistory from '../components/AnswerHistory';
import { Sparkles } from 'lucide-react';
import { saveQuestions, loadQuestions, clearQuestions } from '../lib/indexedDB';

// Classic Magic 8 Ball answers
const MAGIC_ANSWERS = [
  // Affirmative answers
  { text: "It is certain", type: "positive" },
  { text: "It is decidedly so", type: "positive" },
  { text: "Without a doubt", type: "positive" },
  { text: "Yes definitely", type: "positive" },
  { text: "You may rely on it", type: "positive" },
  { text: "As I see it, yes", type: "positive" },
  { text: "Most likely", type: "positive" },
  { text: "Outlook good", type: "positive" },
  { text: "Yes", type: "positive" },
  { text: "Signs point to yes", type: "positive" },
  // Non-committal answers
  { text: "Reply hazy, try again", type: "neutral" },
  { text: "Ask again later", type: "neutral" },
  { text: "Better not tell you now", type: "neutral" },
  { text: "Cannot predict now", type: "neutral" },
  { text: "Concentrate and ask again", type: "neutral" },
  // Negative answers
  { text: "Don't count on it", type: "negative" },
  { text: "My reply is no", type: "negative" },
  { text: "My sources say no", type: "negative" },
  { text: "Outlook not so good", type: "negative" },
  { text: "Very doubtful", type: "negative" },
];

export default function MagicEightBall() {
  const [question, setQuestion] = useState('');
  const [currentAnswer, setCurrentAnswer] = useState(null);
  const [isShaking, setIsShaking] = useState(false);
  const [isRevealing, setIsRevealing] = useState(false);
  const [history, setHistory] = useState([]);
  const [hasAsked, setHasAsked] = useState(false);

  // Load questions from IndexedDB on mount
  useEffect(() => {
    const loadHistory = async () => {
      const savedQuestions = await loadQuestions();
      if (savedQuestions.length > 0) {
        setHistory(savedQuestions);
      }
    };
    loadHistory();
  }, []);

  const getRandomAnswer = useCallback(() => {
    const randomIndex = Math.floor(Math.random() * MAGIC_ANSWERS.length);
    return MAGIC_ANSWERS[randomIndex];
  }, []);

  const handleAskQuestion = useCallback(() => {
    if (!question.trim() || isShaking) return;

    setHasAsked(true);
    setIsShaking(true);
    setCurrentAnswer(null);
    setIsRevealing(false);

    // Shake animation duration
    setTimeout(() => {
      setIsShaking(false);
      const answer = getRandomAnswer();
      setCurrentAnswer(answer);
      setIsRevealing(true);

      // Add to history
      const newHistoryItem = {
        id: Date.now(),
        question: question.trim(),
        answer: answer.text,
        type: answer.type,
        timestamp: new Date()
      };
      
      setHistory(prev => {
        const updatedHistory = [newHistoryItem, ...prev].slice(0, 10); // Keep last 10
        // Save to IndexedDB
        saveQuestions(updatedHistory);
        return updatedHistory;
      });

      // Reset reveal animation
      setTimeout(() => {
        setIsRevealing(false);
      }, 1000);
    }, 800);
  }, [question, isShaking, getRandomAnswer]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      handleAskQuestion();
    }
  }, [handleAskQuestion]);

  const clearHistory = useCallback(async () => {
    setHistory([]);
    await clearQuestions();
  }, []);

  const resetBall = useCallback(() => {
    setQuestion('');
    setCurrentAnswer(null);
    setHasAsked(false);
  }, []);

  return (
    <div className="min-h-screen bg-cosmic relative overflow-hidden">
      {/* Starry Background */}
      <StarryBackground />
      
      {/* Nebula overlay effect */}
      <div className="absolute inset-0 bg-nebula animate-nebula pointer-events-none" />
      
      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 py-8">
        {/* Header */}
        <header className="text-center mb-8 animate-float-slow">
          <div className="flex items-center justify-center gap-3 mb-3">
            <Sparkles className="w-6 h-6 text-primary" />
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground tracking-wide">
              Magic 8 Ball
            </h1>
            <Sparkles className="w-6 h-6 text-primary" />
          </div>
          <p className="text-muted-foreground text-base sm:text-lg max-w-md mx-auto font-body">
            Ask a yes or no question, and let the mystical orb reveal your destiny
          </p>
        </header>

        {/* Question Input */}
        <QuestionInput
          question={question}
          setQuestion={setQuestion}
          onAsk={handleAskQuestion}
          onKeyPress={handleKeyPress}
          isShaking={isShaking}
          hasAsked={hasAsked}
          onReset={resetBall}
        />

        {/* Magic 8 Ball */}
        <div className="my-12">
          <MagicBall
            isShaking={isShaking}
            currentAnswer={currentAnswer}
            isRevealing={isRevealing}
            hasAsked={hasAsked}
          />
        </div>

        {/* Instructions */}
        {!hasAsked && (
          <div className="text-center text-muted-foreground text-sm mb-8 animate-pulse">
            <p>Type your question above and click &ldquo;Ask the Oracle&rdquo; or press Enter</p>
          </div>
        )}

        {/* Answer History */}
        {history.length > 0 && (
          <AnswerHistory history={history} onClear={clearHistory} />
        )}
      </div>

      {/* Footer */}
      <footer className="absolute bottom-4 left-0 right-0 text-center text-muted-foreground/50 text-xs z-20">
        <p className="text-white">Created with ✨ by <a href="https://www.linkedin.com/in/apreshagarwal/" target="_blank" rel="noopener noreferrer" className="hover:underline cursor-pointer">Apresh Agarwal</a></p>
      </footer>
    </div>
  );
}
